// Phase 6.3.1 — SidePanel primitive.
//
// A full-height drawer that slides in from the right (default) or
// left. The "push-don't-cover" intent: on desktop there is NO
// backdrop (the panel sits beside content, which the consumer can
// shrink); on mobile a backdrop dims the content behind.
//
// Phase 6.3.3 (S1.1): optional `resizable` variant — a drag handle
// on the inner edge, width clamped to [minWidth, maxWidth], width
// persisted per-title in localStorage, keyboard Left/Right resize.
// The fixed-size path (resizable omitted/false) is unchanged.
//
// Accessibility: role="dialog", aria-modal, aria-labelledby → title,
// Esc closes, Tab trapped, focus moves in on open + returns on
// close, explicit close (X) button top-right.

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import { getFocusable, trapTabKey } from "./focus";
import { isTopOverlay, popOverlay, pushOverlay } from "./overlayStack";
import { GLASS_SURFACE } from "./Card";

export type SidePanelSize = "sm" | "md" | "lg";
export type SidePanelSide = "right" | "left";

export interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  side?: SidePanelSide;
  size?: SidePanelSize;
  /** S1.1: enable an inner-edge drag handle to resize the panel. */
  resizable?: boolean;
  /** Min width in px when resizable (default 280). */
  minWidth?: number;
  /** Max width in px when resizable (default 800). */
  maxWidth?: number;
  children?: ReactNode;
}

const SIZE_CLASS: Record<SidePanelSize, string> = {
  sm: "w-72",
  md: "w-96",
  lg: "w-[32rem]",
};

// Pixel equivalents of the fixed sizes, used as the initial width in
// resizable mode (before the user has dragged / nothing persisted).
const SIZE_PX: Record<SidePanelSize, number> = { sm: 288, md: 384, lg: 512 };

const DEFAULT_MIN = 280;
const DEFAULT_MAX = 800;
const KEYBOARD_STEP = 16;

function storageKey(title: string): string {
  return `sidepanel-width:${title}`;
}

export function SidePanel({
  open,
  onClose,
  title,
  side = "right",
  size = "md",
  resizable = false,
  minWidth = DEFAULT_MIN,
  maxWidth = DEFAULT_MAX,
  children,
}: SidePanelProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const instanceId = useRef<symbol>(Symbol("sidepanel")).current;

  const clampW = useCallback(
    (n: number) => Math.min(maxWidth, Math.max(minWidth, n)),
    [minWidth, maxWidth],
  );

  const [width, setWidth] = useState<number>(() => {
    if (!resizable) return SIZE_PX[size];
    try {
      const s = localStorage.getItem(storageKey(title));
      if (s) {
        const n = parseInt(s, 10);
        if (Number.isFinite(n)) return clampW(n);
      }
    } catch {
      // ignore — private mode etc.
    }
    return clampW(SIZE_PX[size]);
  });

  // Persist width whenever it changes (drag + keyboard).
  useEffect(() => {
    if (!resizable) return;
    try {
      localStorage.setItem(storageKey(title), String(width));
    } catch {
      // ignore
    }
  }, [resizable, title, width]);

  // ---- drag-to-resize ----
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef<{ x: number; w: number }>({ x: 0, w: 0 });

  const onHandleMouseDown = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragStart.current = { x: e.clientX, w: width };
      setDragging(true);
    },
    [width],
  );

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: globalThis.MouseEvent) => {
      // For a right-side panel the inner (left) edge moves: dragging
      // left (smaller clientX) GROWS the panel. Mirror for left side.
      const delta =
        side === "right"
          ? dragStart.current.x - e.clientX
          : e.clientX - dragStart.current.x;
      setWidth(clampW(dragStart.current.w + delta));
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = prevUserSelect;
    };
  }, [dragging, side, clampW]);

  const onHandleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      e.preventDefault();
      // Right-side panel grows on ArrowLeft; left-side grows on ArrowRight.
      const grow = side === "right" ? e.key === "ArrowLeft" : e.key === "ArrowRight";
      const signed = grow ? KEYBOARD_STEP : -KEYBOARD_STEP;
      setWidth((w) => clampW(w + signed));
    },
    [side, clampW],
  );

  // Shared overlay stack — only the topmost overlay reacts to Esc.
  useEffect(() => {
    if (!open) return;
    pushOverlay(instanceId);
    return () => popOverlay(instanceId);
  }, [open, instanceId]);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusables = getFocusable(panelRef.current);
    (focusables[0] ?? panelRef.current)?.focus();
    return () => {
      previouslyFocused?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape" && isTopOverlay(instanceId)) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, { capture: true });
    return () =>
      document.removeEventListener("keydown", onKey, { capture: true });
  }, [open, onClose, instanceId]);

  const onPanelKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    trapTabKey(e, panelRef.current);
  }, []);

  const onBackdropClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        e.stopPropagation();
        onClose();
      }
    },
    [onClose],
  );

  if (!open) return null;

  return createPortal(
    <div
      data-testid="side-panel-root"
      className={[
        "fixed inset-0 z-50 flex",
        side === "right" ? "justify-end" : "justify-start",
      ].join(" ")}
    >
      {/* Backdrop — mobile only (md:hidden = push-don't-cover on
          desktop). Clicking it closes (mobile). */}
      <div
        data-testid="side-panel-backdrop"
        onClick={onBackdropClick}
        className="absolute inset-0 bg-theme-bg-overlay md:hidden"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="side-panel"
        data-side={side}
        data-size={size}
        data-resizable={resizable ? "true" : "false"}
        tabIndex={-1}
        onKeyDown={onPanelKeyDown}
        style={resizable ? { width: `${width}px` } : undefined}
        data-surface="glass"
        className={[
          // Phase 7.0 S2: frosted glass panel surface + drawer
          // elevation shadow. GLASS_SURFACE supplies the subtle
          // white edge, so the old side-specific theme border is
          // dropped.
          "relative h-full outline-none flex flex-col",
          GLASS_SURFACE,
          "shadow-drawer",
          resizable ? "" : SIZE_CLASS[size],
        ].join(" ")}
      >
        {resizable && (
          <div
            data-testid="side-panel-resize-handle"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panel"
            tabIndex={0}
            onMouseDown={onHandleMouseDown}
            onKeyDown={onHandleKeyDown}
            className={[
              "absolute top-0 h-full w-1 z-10 cursor-col-resize",
              side === "right" ? "left-0" : "right-0",
              "hover:bg-theme-border-emphasis focus-visible:bg-theme-focus-ring focus-visible:outline-none transition-colors duration-150",
            ].join(" ")}
          />
        )}
        <div className="flex items-start justify-between gap-default px-loose py-loose border-b border-theme-border-subtle">
          <h2 id={titleId} data-testid="side-panel-title" className="text-h3">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="side-panel-close"
            aria-label="Close panel"
            className="-mr-1 -mt-1 p-tight rounded-sm text-theme-text-tertiary hover:text-theme-text-primary hover:bg-theme-bg-conv-hover transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-focus-ring"
          >
            <X className="w-4 h-4" strokeWidth={1.5} aria-hidden />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin px-loose py-loose">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
